import { BlockStyleSchema } from "@aurumiq/cms-schema";
import { Button } from "@aurumiq/ui/components/button";
import { Input } from "@aurumiq/ui/components/input";
import { Label } from "@aurumiq/ui/components/label";
import { Switch } from "@aurumiq/ui/components/switch";
import { Tabs, TabsList, TabsTrigger } from "@aurumiq/ui/components/tabs";
import { Textarea } from "@aurumiq/ui/components/textarea";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { cn } from "@aurumiq/ui/lib/utils";
import { ArrowDown, ArrowUp, Copy, Trash2, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { BLOCK_BASE_KEYS, BLOCK_FIELDS, isBlockType } from "../editor/blocks";
import { describe, type FieldSpec } from "../schema-form/introspect";
import { SchemaForm } from "../schema-form/schema-form";
import { BlockIcon, blockTitle } from "./panels";
import type { DesignerBlock, DesignerState } from "./use-designer-state";

export type InspectorTab = "content" | "style";

const STYLE_FIELDS: Record<string, FieldSpec> = (() => {
  const s = describe(BlockStyleSchema);
  return s.kind === "object" ? s.fields : {};
})();

/** Keys rendered by the chrome (or edited elsewhere), not by the Content form. */
const CONTENT_OMIT = [...BLOCK_BASE_KEYS, "style", "label"] as const;
const FREEFORM_GRID_KEYS = ["row_height", "gap", "min_rows"] as const;
const FREEFORM_CONTENT_OMIT = [...CONTENT_OMIT, ...FREEFORM_GRID_KEYS, "elements"] as const;

const POS_KEY = "aurumiq-designer-inspector-pos";

function loadPos(): { x: number; y: number } | null {
  try {
    const v = localStorage.getItem(POS_KEY);
    return v ? (JSON.parse(v) as { x: number; y: number }) : null;
  } catch {
    return null;
  }
}

/**
 * Floating block editor: draggable by its title bar (position remembered per browser), docks
 * back to the top-right. Content tab = schema form; Style tab = BlockStyle form (+ grid settings
 * for free layouts).
 */
export function Inspector({
  st,
  tab,
  onTab,
  onClose,
  onDelete,
}: {
  st: DesignerState;
  tab: InspectorTab;
  onTab: (t: InspectorTab) => void;
  onClose: () => void;
  onDelete: (id: string) => void;
}) {
  const s = st.selected;
  const ref = useRef<HTMLElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(loadPos);

  const startDrag = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const el = ref.current;
    if (!el) return;
    e.preventDefault();
    const r = el.getBoundingClientRect();
    const ox = e.clientX - r.left;
    const oy = e.clientY - r.top;
    const move = (ev: PointerEvent) => {
      const x = Math.min(Math.max(8, ev.clientX - ox), window.innerWidth - r.width - 8);
      const y = Math.min(
        Math.max(8, ev.clientY - oy),
        window.innerHeight - Math.min(r.height, 120),
      );
      setPos({ x, y });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setPos((p) => {
        try {
          if (p) localStorage.setItem(POS_KEY, JSON.stringify(p));
        } catch {
          /* ignore */
        }
        return p;
      });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const dock = () => {
    setPos(null);
    try {
      localStorage.removeItem(POS_KEY);
    } catch {
      /* ignore */
    }
  };

  if (!s) return null;
  return (
    <aside
      ref={ref}
      aria-label="Block inspector"
      className="fixed z-30 flex w-[360px] max-w-[calc(100vw-24px)] flex-col overflow-hidden rounded-2xl border bg-card shadow-[0_24px_60px_-24px_rgba(20,33,61,0.5)]"
      style={
        pos
          ? {
              left: pos.x,
              top: pos.y,
              height: "min(calc(100vh - 32px), 760px)",
              resize: "both",
              minWidth: 300,
              minHeight: 240,
            }
          : { right: 12, top: 64, height: "calc(100vh - 80px)", resize: "both", minWidth: 300 }
      }
    >
      <div
        role="toolbar"
        aria-label="Inspector title bar"
        onPointerDown={startDrag}
        onDoubleClick={dock}
        title="Drag to move · double-click to dock"
        className="flex cursor-grab select-none items-center gap-2 border-b bg-muted/60 px-3 py-1 font-semibold text-[10px] text-muted-foreground uppercase tracking-wider active:cursor-grabbing"
      >
        <span className="text-muted-foreground/60">⋮⋮</span>
        <span className="flex-1">Block editor</span>
        {pos ? (
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={dock}
            className="rounded px-1.5 py-0.5 font-semibold text-[10px] normal-case tracking-normal hover:bg-card hover:text-foreground"
          >
            Dock
          </button>
        ) : null}
      </div>
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded bg-muted text-navy">
          <BlockIcon type={s.type} />
        </span>
        <div className="min-w-0 flex-1">
          <input
            value={typeof s.label === "string" ? s.label : ""}
            placeholder={blockTitle({ ...s, label: undefined })}
            onChange={(e) =>
              st.setBlock(s.id, { ...s, label: e.target.value || undefined }, `${s.id}:label`)
            }
            className="w-full bg-transparent font-bold text-sm outline-none placeholder:text-muted-foreground/70"
            aria-label="Block label"
          />
          <StatusTag className="mt-0.5">{s.type.toUpperCase()}</StatusTag>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="Move up"
          onClick={() => st.moveBy(s.id, -1)}
        >
          <ArrowUp />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="Move down"
          onClick={() => st.moveBy(s.id, 1)}
        >
          <ArrowDown />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="Duplicate block"
          onClick={() => st.duplicateBlock(s.id)}
        >
          <Copy />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-destructive"
          aria-label="Delete block"
          onClick={() => onDelete(s.id)}
        >
          <Trash2 />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label="Close inspector"
          onClick={onClose}
        >
          <X />
        </Button>
      </div>
      <div className="px-4 pt-3">
        <Tabs value={tab} onValueChange={(v) => onTab(v as InspectorTab)}>
          <TabsList className="w-full">
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="style">Style</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-6 pt-3">
        {tab === "content" ? <ContentTab st={st} block={s} /> : <StyleTab st={st} block={s} />}
      </div>
    </aside>
  );
}

function ContentTab({ st, block }: { st: DesignerState; block: DesignerBlock }) {
  const id = useId();
  const v = st.validations[st.page?.blocks.findIndex((b) => b.id === block.id) ?? -1];
  if (!isBlockType(block.type)) return <JsonEditor st={st} block={block} />;
  const freeform = block.type === "freeform";
  const elements = Array.isArray(block.elements) ? block.elements.length : 0;
  return (
    <div className="grid gap-3">
      {v && !v.ok ? (
        <div
          role="alert"
          className="rounded-lg border border-danger/40 bg-danger-bg px-3 py-2 text-danger text-xs"
        >
          {v.issues.length} problem{v.issues.length > 1 ? "s" : ""} — fix the highlighted fields.
        </div>
      ) : null}
      <div>
        <Label htmlFor={`${id}-anchor`}>Anchor (optional)</Label>
        <Input
          id={`${id}-anchor`}
          value={typeof block.anchor === "string" ? block.anchor : ""}
          placeholder="section-id"
          onChange={(e) => {
            const anchor = e.target.value.replace(/[^a-z0-9-]/gi, "-").toLowerCase();
            st.setBlock(block.id, { ...block, anchor: anchor || undefined }, `${block.id}:anchor`);
          }}
        />
      </div>
      {freeform ? (
        <p className="rounded-lg border border-dashed px-3 py-2 text-muted-foreground text-xs">
          {elements} element{elements === 1 ? "" : "s"} — drag, resize and edit them on the canvas.
          Grid settings are under Style.
        </p>
      ) : null}
      <SchemaForm
        fields={BLOCK_FIELDS[block.type]}
        value={block}
        onChange={(next) =>
          st.setBlock(
            block.id,
            { ...next, id: block.id, type: block.type, style: block.style },
            `${block.id}:form`,
          )
        }
        issues={v?.issues}
        omit={freeform ? FREEFORM_CONTENT_OMIT : CONTENT_OMIT}
      />
    </div>
  );
}

function StyleTab({ st, block }: { st: DesignerState; block: DesignerBlock }) {
  const id = useId();
  const style = (block.style as Record<string, unknown> | undefined) ?? {};
  const freeform = block.type === "freeform";
  const gridFields = Object.fromEntries(
    FREEFORM_GRID_KEYS.map((k) => [k, BLOCK_FIELDS.freeform[k]]).filter(([, f]) => f),
  ) as Record<string, FieldSpec>;
  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={`${id}-visible`}>Visible on the live site</Label>
        <Switch
          id={`${id}-visible`}
          checked={block.hidden !== true}
          onCheckedChange={() => st.toggleHidden(block.id)}
        />
      </div>
      <SchemaForm
        fields={STYLE_FIELDS}
        value={style}
        onChange={(next) => st.setStyle(block.id, next)}
      />
      {freeform ? (
        <fieldset className="rounded-lg border p-3">
          <legend className="px-1 font-medium text-sm">Grid</legend>
          <SchemaForm
            fields={gridFields}
            value={block}
            onChange={(next) =>
              st.setBlock(block.id, { ...block, ...next, id: block.id }, `${block.id}:grid`)
            }
          />
        </fieldset>
      ) : null}
    </div>
  );
}

/** Unknown block types are edited as JSON so they can be fixed or removed. */
function JsonEditor({ st, block }: { st: DesignerState; block: DesignerBlock }) {
  const [text, setText] = useState(() => JSON.stringify(block, null, 2));
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setText(JSON.stringify(block, null, 2));
  }, [block]);
  return (
    <div className="grid gap-2">
      <p className="text-muted-foreground text-xs">
        Unknown block type “{block.type}”. The landing site will not render it; set{" "}
        <code className="font-mono">type</code> to a known block or delete it.
      </p>
      <Textarea
        aria-label="Block JSON"
        rows={14}
        className={cn("font-mono text-xs", err && "border-danger")}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          try {
            const parsed = JSON.parse(text) as unknown;
            if (typeof parsed !== "object" || parsed === null) throw new Error("Must be an object");
            setErr(null);
            st.setBlock(block.id, parsed as Record<string, unknown>);
          } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
          }
        }}
      />
      {err ? <p className="text-destructive text-xs">{err}</p> : null}
    </div>
  );
}
